(ns frontend.view.reports.all.view
  (:require
   [clojure.string :as string]
   [common.date-formats :as date-formats]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [common.timezones :as timezones]
   [common.util :as cu]
   [frontend.exports.excel :as excel]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.utils :as fu]
   [frontend.view.components.clipboard :as clipboard]
   [frontend.view.reports.events :as events]
   [frontend.zap-time :as zap-time]
   [potpuri.core :refer [fn-> fn->>]]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.toggle.view :as toggle]
   [ui.editors.data-input :as data-input]))

;;user/admin roles for given id:
(defn account-user-roles
  [account-id]
  (let [roles (->> @session/all-roles
                   (filter session/active-tenant-role?)
                   (filter (fn-> :role/subject (= account-id))))]
    roles))

(defn dynamic-fields
  []
  (let [tenant-schema @session/tenant-schema]
    (->>
     (vec
      (concat (->> (registry/get-schema tenant-schema :entity.type/oppo)
                   (filter (fn [[k fs]]
                             (and (some (set (:field/entity-types fs)) [:entity.type/named-value])
                                  (:named-value-attribute fs)
                                  (f-permissions/field-read-access? k :entity.type/oppo nil))))
                   (keys))
              (->> (registry/get-schema tenant-schema :entity.type/company)
                   (filter (fn [[k fs]]
                             (and (some (set (:field/entity-types fs)) [:entity.type/named-value])
                                  (:named-value-attribute fs)
                                  (f-permissions/field-read-access? k :entity.type/oppo nil))))
                   (keys))))
     (remove (fn [field]
               (some #{:oppo/pass-reason-v2
                       :oppo/lead-source-v2
                       :oppo/funding-round-v2
                       :oppo/lead-type-v2}
                     [field]))))))

(defn reports
  [dynamic-field]
  [[:new-deals-per-person {}]
   (when (f-permissions/field-read-access? :oppo/lead-type-v2 :entity.type/oppo nil)
     [:new-deals-per-lead-type {}])
   (when (f-permissions/field-read-access? :oppo/funding-round-v2 :entity.type/oppo nil)
     [:new-deals-per-funding-round {}])
   (when (f-permissions/field-read-access? :oppo/lead-source-v2 :entity.type/oppo nil)
     [:new-deals-per-lead-source {}])
   (when dynamic-field
     [:new-deals-per-dynamic-field
      {:dynamic-field       dynamic-field
       :dont-identify-opts? true}])
   [:deal-distribution-per-user {}]
   [:deal-distribution-per-user-per-pipeline {}]
   [:new-deals-per-pass-reason {}]
   [:annual-deal-volumes {}]])

;;TODO general enough for a separate components ns?
(defn report-table
  [{:keys [over-columns columns title title-fn data footer state set-sort-key! disable-sort?]}]
  (let [state (or state (r/atom {:reverse-sort? false}))
        set-sort-key! (or set-sort-key!
                          (fn [key]
                            (let [current-sort-key (:sort-by-key @state)]
                              (if (= key current-sort-key)
                                (swap! state update :reverse-sort? not)
                                (swap! state assoc :sort-by-key key :reverse-sort? false)))))
        todays-date (date-formats/format-millis
                     (zap-time/now)
                     (or (:tenant/date-format @session/tenant) (loc/tr :date-format))
                     (or (:tenant/timezone @session/tenant) timezones/default-timezone))]
    (fn [{:keys [columns data info footer disable-sort? title]}]
      (let [{:keys [sort-by-key reverse-sort?]} @state

            ;;column-key might not be keyword:
            data (if-not sort-by-key
                   data
                   (->> data
                        (sort-by (fn [d]
                                   (get d sort-by-key)))))
            data (if reverse-sort?
                   (reverse data)
                   data)
            column-keys (->> columns
                             (map :key))
            export-data (concat [(->> columns
                                      (map :label))]
                                (->> data
                                     (map (fn [data]
                                            (->> column-keys
                                                 (map (fn->> (get data)))))))
                                (when footer
                                  [(->> column-keys
                                        (map (fn->> (get footer))))]))]
        [:div
         {:class "flex flex-col relative items-start"}
         [:div
          {:class "mb-2 flex w-full justify-between flex-row-reverse"}
          [:div
           {:class "w-64 flex justify-end gap-1 absolute -top-6 right-0"}
           (when info
             [button/square-secondary-open
              {:tooltip info}
              [:i.fal.fa-info-circle]])
           [button/square-secondary-open
            {:title    (loc/tr :copy-to-clipboard)
             :on-click (fn [_]
                         (clipboard/copy-as-tsv export-data))}
            [:i.fal.fa-copy]]
           [button/square-secondary-open
            {:title    (loc/tr :save-as-excel)
             :on-click (fn [_]
                         (excel/export-array-data
                          (loc/tr :export-filename-report
                                  [(or (when title-fn
                                         (title-fn))
                                       title)
                                   todays-date])
                          export-data))}
            [:i.fal.fa-file-excel]]]]
         [:div
          {:class "max-w-full overflow-auto"}
          [:table
           {:class-name "min-w-full divide-y divide-gray-300 border border-solid border-base-light-border-paler"}

           [:thead
            {:class "divide-y divide-solid divide-base-light-border-paler"}
            (when (seq over-columns)
              [:tr
               {:class "divide-x divide-solid divide-base-light-border-paler"}
               [:th
                {:col-span "1"
                 :class    "py-3.5 pl-4 pr-4 text-left text-sm font-bold text-base-light-text sm:pl-0"}
                [:span
                 [:span ""]]]
               (doall
                (for [column over-columns
                      :let [column-key (:key column)
                            label (:label column)
                            col-span (:col-span column)]]
                  ^{:key column-key}
                  [:th
                   {:col-span col-span
                    :class    "px-4 py-3.5 text-left text-sm font-bold text-base-light-text"}
                   [:span
                    [:span label]]]))])
            [:tr
             {:class "divide-x divide-solid divide-base-light-border-paler"}
             (->> columns
                  (map-indexed
                   (fn [index column]
                     (let [column-key (:key column)
                           column-class (:class-name column)
                           label (:label column)]
                       ^{:key column-key}
                       [:th
                        {:class "px-3 py-2 text-left text-sm font-bold text-base-light-text border-b border-solid border-base-light-border-paler"}
                        [:span
                         {:class-name (fu/cs "flex items-center gap-3"
                                             column-class)
                          :on-click   (when-not disable-sort? (partial set-sort-key! column-key))
                          :style      {:justify-content (if (and disable-sort?
                                                                 (not= index 0))
                                                          "center"
                                                          "space-between")}}
                         [:span
                          label]
                         (when-not disable-sort?
                           (cond (and (= column-key sort-by-key)
                                      reverse-sort?)
                                 [:i.fal.fa-sort-down]
                                 (= column-key sort-by-key)
                                 [:i.fal.fa-sort-up]
                                 :else
                                 [:i.fal.fa-sort]))]])))
                  (doall))]]
           [:tbody
            {:class "divide-y divide-solid divide-base-light-border-paler"}
            (->> data
                 (map (fn [element]
                        (let [id (:id element)]
                          ^{:key id}
                          [:tr
                           {:class-name "divide-x divide-solid divide-base-light-border-paler"}
                           (->> columns
                                (map-indexed (fn [index column]
                                               (let [column-key (:key column)
                                                     value (get element column-key)]
                                                 ^{:key (str id "-" column-key)}
                                                 [:td
                                                  {:class "whitespace-nowrap py-2 pl-3 pr-3 text-sm font-medium text-base-light-text"}
                                                  value])))
                                (doall))])))
                 (doall))]

           (when footer
             [:tfoot
              [:tr
               {:class-name "divide-x divide-solid divide-base-light-border-paler"}
               (doall
                (for [column columns
                      :let [column-key (:key column)]]
                  ^{:key (str "footer-" column-key)}
                  [:td
                   {:class "whitespace-nowrap py-2 pl-3 pr-3 text-sm font-bold text-base-light-text border-t border-solid border-base-light-border-paler bg-base-regular"}
                   [:strong (get footer column-key)]]))]])]]]))))

(defn new-deal-report-title
  [kw dynamic-field-state]
  (if (= kw :new-deals-per-dynamic-field)
    (loc/tr :reports/per-tag)
    (loc/tr (keyword "reports" (name kw)))))

(defn main-column-label
  [kw dynamic-field-state]
  (let [labels
        (merge {:new-deals-per-person      (loc/tr :team-member)
                :new-deals-per-owner       (loc/tr :common/owner)
                :new-deals-per-pass-reason (loc/tr :pass-reason)}
               (when (f-permissions/field-read-access? :oppo/funding-round-v2 :entity.type/oppo nil)
                 {:new-deals-per-funding-round (loc/tr :oppo/funding-round-v2)})
               (when (f-permissions/field-read-access? :oppo/lead-type-v2 :entity.type/oppo nil)
                 {:new-deals-per-lead-type (loc/tr :oppo/lead-type-v2)})
               (when (f-permissions/field-read-access? :oppo/lead-source-v2 :entity.type/oppo nil)
                 {:new-deals-per-lead-source (loc/tr :oppo/lead-source-v2)}))]
    (if (= kw :new-deals-per-dynamic-field)
      (loc/tr (:dynamic-field @dynamic-field-state))
      (get labels kw))))

(defn- disabled-account?
  [{:keys [id]}]
  (empty? (account-user-roles id)))

(defn- grey-out-if-disabled
  [disabled-fn? element]
  (if (disabled-fn? element)
    (assoc element :class-name "text-disabled-light-text")
    element))

(defn handle-disabled-accounts
  [show-disabled-users? accounts]
  (if show-disabled-users?
    (map (partial grey-out-if-disabled disabled-account?) accounts)
    (remove disabled-account? accounts)))

(defn inactive-pipelines-selector
  [state]
  [:span
   {:class "inline-flex items-center mb-2"}
   [toggle/toggle
    {:checked?  (boolean (:show-archived-pipelines? @state))
     :on-change (fn [checked?]
                  (swap! state assoc :show-archived-pipelines? checked?))}]
   [:span.d-flex.input-editor__label.text-regular.white-space-nowrap.ml-2
    (loc/tr :show-archived-pipelines)]])

(defn annual-deal-volumes
  [state]
  (let [report-data (events/get-data! :annual-deal-volumes)
        title (loc/tr :reports/annual-deal-volumes)
        info-kw :report-info/annual-deal-volumes
        info (loc/tr info-kw)

        report-with-deal-entities
        (->> report-data
             (mapv (fn [{:keys [key data]}]
                     (let [data-with-entities
                           (zipmap (keys data)
                                   (->> (vals data)
                                        (mapv (fn [{:keys [key data count]}]
                                                (let [deal-entities (->> data
                                                                         (mapv (fn [id]
                                                                                 (session/get-entity
                                                                                  id))))]
                                                  {:key key :data deal-entities :count count})))))]
                       {:key key :data data-with-entities}))))

        all-oppo-pipelines (->> @session/pipelines
                                (filter session/has-entity-access?)
                                (filter (fn [pipeline]
                                          (= (:pipeline/entity-type pipeline) :entity.type/oppo))))

        inactive-pipelines? (->> all-oppo-pipelines
                                 (filter session/has-entity-access?)
                                 (filter (fn [pipeline]
                                           (:pipeline/inactive? pipeline)))
                                 (count)
                                 (< 0))

        pipelines (->> @session/pipelines
                       (filter session/has-entity-access?)
                       (filter (fn [pipeline]
                                 (= (:pipeline/entity-type pipeline) :entity.type/oppo)))
                       (remove (fn [pipeline]
                                 (when (not (:show-archived-pipelines? @state))
                                   (:pipeline/inactive? pipeline)))))

        multiple-pipelines? (> (count all-oppo-pipelines) 1)

        report-data-by-pipeline
        (->>
         pipelines
         (mapv :db/id)
         (map
          (fn [pipeline-id]
            (let [data
                  (->> report-with-deal-entities
                       (map
                        (fn [{:keys [key data]}]
                          (let [data-for-this-pipeline
                                (zipmap
                                 (keys data)
                                 (->> (vals data)
                                      (map (fn [{:keys [key data]}]
                                             (let [oppos-for-pipeline
                                                   (->> data
                                                        (filter (fn [oppo]
                                                                  (= (:oppo/pipeline oppo)
                                                                     pipeline-id))))
                                                   oppo-count (count oppos-for-pipeline)]

                                               {:key   key
                                                :data  oppos-for-pipeline
                                                :count oppo-count})))))]

                            {:key key :data data-for-this-pipeline}))))]

              [pipeline-id data])))
         (into {}))

        years (->> report-data
                   (map :key))
        volumes-columns (->> years
                             sort
                             (map (fn [k]
                                    {:key   k
                                     :label k}))
                             (concat [{:key   :title
                                       :label "Deals"}]))
        get-yearly-data (fn [year id]
                          (let [full-data (->> report-data
                                               (filter (fn-> :key (= year)))
                                               first
                                               :data)
                                id-data (get full-data id)]
                            {year (or (:count id-data) 0)}))
        get-datas (fn [id]
                    (->> years
                         (map (fn-> (get-yearly-data id)))
                         (into {})))

        get-pipeline-yearly-data (fn [year pipeline-id data-id]
                                   (let [data-for-pipeline (get report-data-by-pipeline pipeline-id)
                                         full-data (->> data-for-pipeline
                                                        (filter (fn-> :key (= year)))
                                                        first
                                                        :data)
                                         id-data (get full-data data-id)]
                                     {year (or (:count id-data) 0)}))

        get-datas-for-pipeline (fn [pipeline-id data-id]
                                 (->> years
                                      (map (fn-> (get-pipeline-yearly-data pipeline-id data-id)))
                                      (into {})))

        static-rows [{:id    :volume-data/deals-seen
                      :title "Seen"}
                     {:id    :volume-data/deals-on-hold
                      :title "Put on hold"}
                     {:id    :volume-data/deals-passed
                      :title "Passed"}
                     {:id    :volume-data/deals-reactivated
                      :title "Reactivated"}
                     {:id    :volume-data/deals-to-portfolio
                      :title "Invested in"}]

        active-stages (->> @session/all-stages
                           (remove :common/disabled?)
                           (sort-by :common/ord))
        stage-rows (->> active-stages
                        (map (fn [stage]
                               {:id          (:db/id stage)
                                :title       (:stage/name stage)
                                :pipeline-id (:stage/pipeline stage)})))

        overall-volumes-data (->> static-rows
                                  (map (fn [{:keys [id] :as el}]
                                         (merge el (get-datas id)))))]
    [:div
     {:class "relative max-w-full"}
     [:div
      {:class "flex flex-col items-start gap-4"}
      (when multiple-pipelines?
        [:<>
         [:div
          {:class "pb-2 flex items-center font-bold"}
          (loc/tr :deal-flow-all-pipelines)]
         [:div {:style {:height "25px"}}]
         [report-table
          {:columns       volumes-columns
           :info          info
           :title         title
           :data          overall-volumes-data
           :disable-sort? true}]
         [:div.w-100.mb-2]])
      (when multiple-pipelines?
        [:div
         {:class "pb-2 flex items-center font-bold"}
         (loc/tr :deal-flow-by-pipeline)])
      (when inactive-pipelines?
        [inactive-pipelines-selector state])
      [:div.mt-2 {:style {:max-width "100%"}}
       (->>
        pipelines
        (sort-by (fn [pipeline]
                   (:pipeline/name pipeline)))
        (map
         (fn [pipeline]
           (let [pipeline-id (:db/id pipeline)
                 pipeline-name (:pipeline/name pipeline)
                 inactive? (:pipeline/inactive? pipeline)
                 stages-columns (->> years
                                     sort
                                     (map (fn [k]
                                            {:key   k
                                             :label k}))
                                     (concat [{:key   :title
                                               :label "Completed stages"}]))
                 pipeline-stages-data
                 (->> stage-rows
                      (filter (fn [stage-row]
                                (= (:pipeline-id stage-row) pipeline-id)))
                      (map (fn [{:keys [id] :as el}]
                             (merge el (get-datas-for-pipeline pipeline-id id)))))
                 pipeline-volumes-data
                 (->> static-rows
                      (map (fn [{:keys [id] :as el}]
                             (merge el (get-datas-for-pipeline pipeline-id id)))))]
             [:div
              {:key   pipeline-id
               :style {:align-items "start" :margin-top (when-not multiple-pipelines? "-20px")}}
              (when multiple-pipelines?
                [:p.mb-1
                 (str (loc/tr :pipeline)
                      ": "
                      (:pipeline/name pipeline)
                      (when inactive? (str " (" (loc/tr :archived) ")")))])
              [:div.deal-volumes-tables-container
               [report-table
                {:columns       volumes-columns
                 :info          info
                 :data          pipeline-volumes-data
                 :disable-sort? true}]

               [report-table
                {:columns       stages-columns
                 :info          info
                 :data          pipeline-stages-data
                 :disable-sort? true}]]
              [:div {:style {:height "30px"}}]])))
        (doall))]]]))

(defn new-deal-report-view
  [{:keys [kw dynamic-field-state show-disabled-users?]}]
  (let [data (events/get-data! kw)
        columns [{:key :dimension :label (main-column-label kw dynamic-field-state)}
                 {:key :t14d :label (loc/tr :t14d)}
                 {:key :t30d :label (loc/tr :t30d)}
                 {:key :t90d :label (loc/tr :t90d)}
                 {:key :t365d :label (loc/tr :t365d)}
                 {:key :mtd :label (loc/tr :mtd)}
                 {:key :ytd :label (loc/tr :ytd)}
                 {:key :all-time :label (loc/tr :all-time)}]
        title (new-deal-report-title kw dynamic-field-state)

        dynamic-field (:dynamic-field @dynamic-field-state)
        available-fields (dynamic-fields)

        info-kw (keyword "report-info" (name kw))
        info (if (= kw :new-deals-per-dynamic-field)
               (loc/tr :report-info/new-deals-per-dynamic-field [(loc/tr dynamic-field)])
               (loc/tr info-kw))

        footer-columns (->> columns
                            (map :key)
                            (remove (fn-> (= :dimension))))
        footer (->> footer-columns
                    (map (fn [k]
                           [k
                            (->> data
                                 (map k)
                                 (reduce +))]))
                    (into {}))
        dynamic-attribute-table? (= kw :new-deals-per-dynamic-field)

        footer (if-not dynamic-attribute-table?
                 (assoc footer :dimension "Total")
                 nil)

        account-report? (contains? #{:new-deals-per-person :new-deals-per-owner} kw)
        data (if account-report?
               (handle-disabled-accounts show-disabled-users? data)
               data)
        data (->> data
                  (map (fn [{:keys [id] :as el}]
                         (assoc el
                                :id
                                (str id
                                     (:dimension el)))))
                  (map (fn [el]
                         (if (keyword? (:dimension el))
                           (assoc el
                                  :dimension (-> el
                                                 :dimension
                                                 name
                                                 string/capitalize)
                                  :class-name "unknown")
                           el))))]
    [:div {:class "mb-4 max-w-full"}
     [:div
      {:class "pb-2 flex items-center font-bold"}
      [:span
       {:class "me-3 inline-block"}
       title]
      (when (and (= kw :new-deals-per-dynamic-field)
                 dynamic-field
                 (cu/multiple? available-fields))
        [:div
         {:class "w-44"}

         [data-input/view
          {:editor-type :editor.type/select
           :data-type   :data.type/keyword
           :full-width? true
           :value       dynamic-field
           :options     (->> available-fields
                             (map (fn [k]
                                    {:value k
                                     :label (loc/tr k)})))
           :on-change   (fn [new-value & _]
                          (swap! dynamic-field-state assoc :dynamic-field new-value)
                          (events/fetch-data! [:new-deals-per-dynamic-field
                                               {:dynamic-field       new-value
                                                :dont-identify-opts? true}]))}]])]
     [report-table
      {:columns  columns
       :title    title
       :title-fn (partial new-deal-report-title kw dynamic-field-state)
       :info     info
       :data     data
       :footer   footer}]]))

(defn deal-distribution-per-user
  [state]
  (let [report-data (events/get-data! :deal-distribution-per-user)
        local-state (r/atom {:show-archived-pipelines? true})
        info-kw :report-info/deal-distribution-per-user
        info (loc/tr info-kw)
        [title orig-columns & data-and-footer] report-data
        orig-data (butlast data-and-footer)
        orig-footer (last data-and-footer)
        ;;data transformations:

        report-by-pipeline (events/get-data! :deal-distribution-per-user-per-pipeline)

        inactive-pipelines? (->> @session/all-oppo-pipelines
                                 (filter (fn [pipeline]
                                           (:pipeline/inactive? pipeline)))
                                 (count)
                                 (< 0))

        pipelines (->> (keys report-by-pipeline)
                       (map (fn [pipeline-id]
                              (session/get-entity pipeline-id)))
                       (sort-by :pipeline/name)
                       (remove (fn [pipeline]
                                 (when (not (:show-archived-pipelines? @state))
                                   (:pipeline/inactive? pipeline))))
                       (remove nil?))

        multiple-pipelines? (< 1 (count pipelines))

        data (->> data-and-footer
                  (map (fn [element]
                         (->> element
                              rest
                              (map-indexed (fn [i val]
                                             [i val]))
                              (concat [[:id (first element)]])
                              (into {})))))
        footer (last data)
        data (butlast data)
        show-disabled-users? (:show-disabled-users? @state)
        data (->> data
                  (handle-disabled-accounts show-disabled-users?))]

; TODO this is the old and wrong way. Leave it as comment for reference if some customer wants it back
    #_[:<>
       [:div.report-title.mb-1 title]
       [report-table
        {:columns columns
         :data    data
         :info    info
         :footer  footer
         :title   (loc/tr :reports/deal-distribution-per-user)}]]
    [:div
     {:class "relative w-full"}
     #_[:div.report-title.mb-1 title]
     (when inactive-pipelines?
       [inactive-pipelines-selector state])
     [:<>
      [:div
       {:class "flex flex-col items-start"}
       (->>
        pipelines
        (map
         (fn [pipeline]
           (let [pipeline-id (:db/id pipeline)
                 inactive? (:pipeline/inactive? pipeline)
                 [title orig-columns & data-and-footer] (get report-by-pipeline pipeline-id)
                 columns (->> orig-columns
                              rest
                              (map-indexed (fn [i {:keys [title width column-type]}]
                                             {:key         i
                                              :label       title
                                              :class-name  (when width
                                                             "unknown")
                                              :column-type column-type}))
                              (drop 1)
                              (concat [{:key         0
                                        :label       (loc/tr :user)
                                        :class-name  "deal-distribution-user"
                                        :column-type :info}]))
                 stage-col-count (count (->> columns
                                             (filter (fn [col] (= (:column-type col) :stage)))))
                 status-col-count (count (->> columns
                                              (filter (fn [col] (= (:column-type col) :status)))))
                 over-columns
                 [{:label (loc/tr :stage) :col-span (str stage-col-count) :key "stage-over-col"}
                  {:label (loc/tr :status) :col-span (str status-col-count) :key "status-over-col"}]

                 data (->> data-and-footer
                           (map (fn [element]
                                  (->> element
                                       rest
                                       (map-indexed (fn [i val]
                                                      [i val]))
                                       (concat [[:id (first element)]])
                                       (into {})))))
                 footer (last data)
                 data (butlast data)
                 show-disabled-users? (:show-disabled-users? @state)
                 data (->> data
                           (handle-disabled-accounts show-disabled-users?))]
             [:div {:key pipeline-id :style {:margin-bottom "40px" :max-width "100%"}}
              (when multiple-pipelines?
                [:p
                 {:class "pb-2"}
                 (str (loc/tr :pipeline)
                      ": "
                      (:pipeline/name pipeline)
                      (when inactive? (str " (" (loc/tr :archived) ")")))])
              [report-table
               {:over-columns over-columns
                :columns      columns
                :data         data
                :info         info
                :footer       footer
                :title        (loc/tr :reports/deal-distribution-per-user)}]])))
        (doall))]]]))

(defn new-investment-opportunities
  [local-state kws show-disabled-users?]
  [:div
   {:class "p-4"}
   [:div
    {:class "pb-4 flex items-center font-semibold text-xl"}
    (loc/tr :new-investment-opportunities)]
   [:div
    {:class "flex flex-col items-start gap-4"}
    (doall
     (for [kw kws]
       ^{:key (str kw)}
       [new-deal-report-view
        {:kw                   kw
         :dynamic-field-state  local-state
         :show-disabled-users? show-disabled-users?}]))]])

(defn deal-flow-summary
  [state kws]
  (let [deal-flow-state (r/atom {:show-archived-pipelines? false})]
    (fn [state]
      [:div
       {:class "p-4"}
       [:div
        {:class "pb-4 flex items-center font-semibold text-xl"}
        (loc/tr :deal-flow-summary)]
       [:div
        [annual-deal-volumes deal-flow-state]]])))

(defn deal-volumes-by-user-and-stage
  [state]
  [:div
   {:class "p-4"}
   [:div {:class "pb-4 flex items-center font-semibold text-xl"}
    (loc/tr :deal-flow-volumes)]
   [:div
    {:class "flex flex-col items-start"}
    [deal-distribution-per-user state]]])

(defn view
  [_]
  (let [available-fields (dynamic-fields)
        local-state (r/atom {:dynamic-field (first available-fields)})]
    (doall (map events/fetch-data! (reports (:dynamic-field @local-state))))
    (r/create-class
     {:component-will-unmount
      (fn []
        (doall (map events/delete-data! (reports (:dynamic-field @local-state)))))
      :reagent-render
      (fn [{:keys [state]}]
        (let [new-deal-kws (concat [:new-deals-per-person
                                    :new-deals-per-owner]
                                   (when (f-permissions/field-read-access? :oppo/funding-round-v2
                                                                           :entity.type/oppo
                                                                           nil)
                                     [:new-deals-per-funding-round])
                                   (when (f-permissions/field-read-access? :oppo/lead-type-v2
                                                                           :entity.type/oppo
                                                                           nil)
                                     [:new-deals-per-lead-type])
                                   (when (f-permissions/field-read-access? :oppo/lead-source-v2
                                                                           :entity.type/oppo
                                                                           nil)
                                     [:new-deals-per-lead-source])
                                   (when (f-permissions/field-read-access? :oppo/pass-reason-v2
                                                                           :entity.type/oppo
                                                                           nil)
                                     [:new-deals-per-pass-reason])
                                   [:new-deals-per-dynamic-field])
              activity-kws [:annual-deal-volumes
                            :deal-distribution-per-user]]
          [:div
           [new-investment-opportunities local-state new-deal-kws (:show-disabled-users? @state)]
           [deal-flow-summary state activity-kws]
           [deal-volumes-by-user-and-stage state]]))})))
